import { permissions } from "sharedresources";

export const ROUTE_PERMISSIONS: Record<string, string | string[]> = {
    // Dashboard routes
    '/dashboard': permissions.PERMISSIONS.DASHBOARD_VIEW,

    // Account management routes
    '/dashboard/accounts': permissions.PERMISSIONS.ACCOUNT_VIEW,

    // User/Participant management routes
    '/dashboard/users': permissions.PERMISSIONS.PARTICIPANT_VIEW,
    '/dashboard/students': permissions.PERMISSIONS.PARTICIPANT_VIEW,
    '/dashboard/staffs': permissions.PERMISSIONS.TEAM_VIEW,
    '/dashboard/staffs/staffDetailView': permissions.PERMISSIONS.TEAM_VIEW,

    // Booking routes
    '/dashboard/booking': permissions.PERMISSIONS.BOOKING_VIEW,
    '/dashboard/booking/bookingDetail': permissions.PERMISSIONS.BOOKING_VIEW,
    '/dashboard/book': permissions.PERMISSIONS.BOOKING_ADD,

    // Calendar routes
    '/dashboard/calendar': permissions.PERMISSIONS.CALENDAR_VIEW,

    // Attendance routes
    '/dashboard/attendance': permissions.PERMISSIONS.ATTENDANCE_VIEW,

    // Reports routes (allow if user has any of the listed permissions)
    '/dashboard/reports': [
        permissions.PERMISSIONS.REPORTS_GENERATE,
        permissions.PERMISSIONS.REPORTS_DOWNLOAD,
        permissions.PERMISSIONS.REPORTS_DELETE
    ],

    // Payment routes
    '/dashboard/payments': permissions.PERMISSIONS.PAYMENT_VIEW,
    '/dashboard/transactions': permissions.PERMISSIONS.PAYMENT_VIEW,
    '/dashboard/invoices/createInvoice': permissions.PERMISSIONS.PAYMENT_EDIT,
    '/dashboard/invoices/invoiceDetail': permissions.PERMISSIONS.PAYMENT_VIEW,

    // Package routes
    '/dashboard/package': permissions.PERMISSIONS.PACKAGE_VIEW,
    '/dashboard/package/packageDetailedView': permissions.PERMISSIONS.PACKAGE_VIEW,
    '/dashboard/package/createPackage': permissions.PERMISSIONS.PACKAGE_ADD,
    '/dashboard/classPacks': permissions.PERMISSIONS.PACKAGE_ADD,

    // Team management routes
    '/dashboard/teams': permissions.PERMISSIONS.TEAM_VIEW,

    // Multi-center routes
    '/dashboard/multicenter': permissions.PERMISSIONS.SYSTEM_SETTINGS_CENTERS_CONFIGURATION,

    // Timeline/Activity routes
    '/dashboard/timeline': permissions.PERMISSIONS.FEED_VIEW,
    '/dashboard/activity': permissions.PERMISSIONS.FEED_VIEW,
    '/dashboard/timeline/createActivity': permissions.PERMISSIONS.FEED_CREATE_POST,
    '/dashboard/timeline/editActivity': permissions.PERMISSIONS.FEED_EDIT_POST,
    '/dashboard/timeline/deleteActivity': permissions.PERMISSIONS.FEED_DELETE,
    '/dashboard/timeline/activityDetail': permissions.PERMISSIONS.FEED_VIEW,

    // Settings routes
    '/dashboard/setting': permissions.PERMISSIONS.SYSTEM_SETTINGS_COMPANY_SETTINGS,
    // Feature flags: not linked anywhere, super admins only (SYSTEM_SETTINGS_* is super-admin only).
    '/dashboard/flagSettings': permissions.PERMISSIONS.SYSTEM_SETTINGS_COMPANY_SETTINGS,

    //account routes
    '/dashboard/accounts/accountDetailView': permissions.PERMISSIONS.ACCOUNT_VIEW,

    //participant routes
    '/dashboard/users/userDetailView': permissions.PERMISSIONS.PARTICIPANT_VIEW,

    //package routes
    '/dashboard/package/packageView': permissions.PERMISSIONS.PACKAGE_VIEW,
};

export function getRequiredPermission(pathname: string): string | string[] | null {
    // Remove query parameters for matching
    const pathWithoutQuery = pathname.split('?')[0];

    // Direct match first
    if (ROUTE_PERMISSIONS[pathWithoutQuery]) {
        return ROUTE_PERMISSIONS[pathWithoutQuery];
    }

    // Check for nested routes (e.g., /dashboard/accounts/edit should match /dashboard/accounts)
    // Sort routes by length (longest first) to match more specific routes before less specific ones
    const sortedRoutes = Object.entries(ROUTE_PERMISSIONS).sort((a, b) => b[0].length - a[0].length);
    for (const [route, permission] of sortedRoutes) {
        if (pathWithoutQuery.startsWith(route + '/') || pathWithoutQuery === route) {
            return permission;
        }
    }

    return null;
}

export function hasRoutePermission(firebase: any, pathname: string): boolean {
    // Allow everyone access to /onBoarding
    if (pathname === '/onBoarding' || pathname.startsWith('/onBoarding/')) {
        return true;
    }

    const requiredPermission = getRequiredPermission(pathname);

    // If no permission required, allow access
    if (!requiredPermission) {
        return true;
    }

    const teacher = firebase?.teacher;
    if (!teacher) {
        return false;
    }
    // Check if this is a SYSTEM_SETTINGS permission - only allow super admin
    if (!Array.isArray(requiredPermission) && requiredPermission.startsWith('SYSTEM_SETTINGS_')) {
        return teacher.isSuperAdmin;
    }

    // Super admin has all permissions
    if (teacher.isSuperAdmin || teacher.role == "Super Admin") {
        return true;
    }
    // If multiple permissions are provided for a route, allow access if user has any
    const requiredPermissionsArray: string[] = Array.isArray(requiredPermission)
        ? requiredPermission
        : [requiredPermission];

    // Check if teacher has any of the required permissions in any of their centers
    const permissionMap = teacher.permissionMap || {};
    return Object.values(permissionMap).some(centerPermissions => {
        if (Array.isArray(centerPermissions)) {
            return requiredPermissionsArray.some(p => centerPermissions.includes(p));
        }
        return false;
    });
}

export function getDefaultRoute(firebase: any): string {
    const teacher = firebase?.teacher;
    if (!teacher) {
        return '/dashboard';
    }

    // Super admin can access everything, default to dashboard
    if (teacher.isSuperAdmin) {
        return '/dashboard';
    }

    const permissionMap = teacher.permissionMap || {};
    const userPermissions = new Set<string>();

    // Collect all user permissions
    Object.values(permissionMap).forEach(centerPermissions => {
        if (Array.isArray(centerPermissions)) {
            centerPermissions.forEach(permission => userPermissions.add(permission));
        }
    });

    // Priority order for default routes
    const routePriority = [
        '/dashboard',
        '/dashboard/booking',
        '/dashboard/calendar',
        '/dashboard/users',
        '/dashboard/accounts',
        '/dashboard/reports',
        '/dashboard/payments',
        '/dashboard/package',
        '/dashboard/teams',
        '/dashboard/multicenter',
        '/dashboard/attendance',
        '/dashboard/timeline',
        '/dashboard/multicenter',
    ];

    // Find the first route the user has permission for
    for (const route of routePriority) {
        if (hasRoutePermission(firebase, route)) {
            return route;
        }
    }

    // Fallback to dashboard
    return '/dashboard';
}
